<template>
  <auth-layout :md="8" :lg="7" :xl="6">
    <auth-header title="Vacaciones Útiles UNMSM" subtitle="Crea tu cuenta de Apoderado" />

    <!-- AVISO OFICIAL (VISIBLE) -->
    <p class="official-notice">
      Sistema oficial de matrícula de Vacaciones Útiles – UNMSM.
      No solicitamos información fuera del proceso institucional.
    </p>

    <ds-card :elevation="12" variant="elevated" class="register-card">
      <template #content>
        <ds-alert v-if="errorMessage" type="error" variant="tonal" :message="errorMessage" closable
          class="register-card__alert" @close="clearError" />

        <ds-form ref="formRef" v-model="isFormValid" :show-actions="false" fields-gap="md" @submit="handleSubmit">
          <v-row>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.nombres" label="Nombres" placeholder="Ingresa tus nombres"
                prepend-inner-icon="mdi-account-outline" :rules="nombresRules" :disabled="isLoading"
                ds-variant="outlined" ds-color="primary" autocomplete="given-name" clearable
                @blur="validateField('nombres')" />
            </v-col>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.apellidos" label="Apellidos" placeholder="Ingresa tus apellidos"
                prepend-inner-icon="mdi-account-outline" :rules="apellidosRules" :disabled="isLoading"
                ds-variant="outlined" ds-color="primary" autocomplete="family-name" clearable
                @blur="validateField('apellidos')" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <ds-select v-model="formData.tipoDocumento" label="Tipo de Documento" :items="[...documentTypes]"
                :rules="tipoDocumentoRules" :disabled="isLoading" ds-variant="outlined" ds-color="primary"
                @update:model-value="validateField('dni')" />
            </v-col>
            <v-col cols="12" sm="6" class="register-card__document-number-col">
              <ds-text-field v-model="formData.dni" label="Número de Documento"
                placeholder="Ingresa tu número de documento" prepend-inner-icon="mdi-card-account-details-outline"
                :rules="dniRules" :disabled="isLoading" ds-variant="outlined" ds-color="primary" clearable
                @blur="validateField('dni')" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.email" label="Correo Electrónico" type="email"
                placeholder="correo@ejemplo.com" prepend-inner-icon="mdi-email-outline" :rules="emailRules"
                :disabled="isLoading" ds-variant="outlined" ds-color="primary" autocomplete="email" clearable
                @blur="validateField('email')" />
            </v-col>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.telefono" label="Teléfono" type="tel" placeholder="999999999"
                prepend-inner-icon="mdi-phone-outline" :rules="telefonoRules" :disabled="isLoading"
                ds-variant="outlined" ds-color="primary" autocomplete="tel" clearable
                @blur="validateField('telefono')" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.password" :type="showPassword ? 'text' : 'password'" label="Contraseña"
                placeholder="••••••••" prepend-inner-icon="mdi-lock-outline"
                :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append-inner="togglePassword"
                :rules="passwordRules" :disabled="isLoading" ds-variant="outlined" ds-color="primary"
                autocomplete="new-password" @blur="validateField('password')" />
            </v-col>
            <v-col cols="12" md="6">
              <ds-text-field v-model="formData.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                label="Confirmar Contraseña" placeholder="••••••••" prepend-inner-icon="mdi-lock-check-outline"
                :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="toggleConfirmPassword" :rules="confirmPasswordRules" :disabled="isLoading"
                ds-variant="outlined" ds-color="primary" autocomplete="new-password"
                @blur="validateField('confirmPassword')" />
            </v-col>
          </v-row>

          <div class="register-card__password-hint">
            <ds-icon icon="mdi-information-outline" size="small" />
            <span class="register-card__password-hint-text">
              La contraseña debe tener al menos 8 caracteres
            </span>
          </div>

          <ds-button type="submit" ds-variant="primary" size="large" block :loading="isLoading"
            :disabled="!isFormValid || isLoading" class="register-card__submit">
            <template v-if="!isLoading">
              Crear Cuenta
            </template>
            <template v-else>
              Creando cuenta...
            </template>
          </ds-button>

          <v-divider class="register-card__divider" />

          <div class="register-card__login">
            <p class="register-card__login-text">
              ¿Ya tienes una cuenta?
            </p>
            <ds-button :to="{ name: 'Login' }" ds-variant="secondary" size="large" block
              class="register-card__login-btn">
              Iniciar Sesión
            </ds-button>
          </div>
        </ds-form>
      </template>
    </ds-card>

    <auth-footer />
  </auth-layout>
</template>

<script setup lang="ts">
import { DsButton, DsTextField, DsIcon } from '@/shared/components/atoms'
import { DsAlert, DsSelect } from '@/shared/components/molecules'
import { DsCard, DsForm } from '@/shared/components/organisms'
import { AuthLayout, AuthHeader, AuthFooter } from '@/modules/auth/components'
import { useRegister } from './RegisterView'

const {
  formRef,
  formData,
  isFormValid,
  isLoading,
  showPassword,
  showConfirmPassword,
  errorMessage,
  documentTypes,
  nombresRules,
  apellidosRules,
  tipoDocumentoRules,
  dniRules,
  emailRules,
  telefonoRules,
  passwordRules,
  confirmPasswordRules,
  handleSubmit,
  togglePassword,
  toggleConfirmPassword,
  validateField,
  clearError,
  handleGoToLogin,
} = useRegister()
</script>

<style scoped lang="scss" src="./RegisterView.scss" />
